import React, { useMemo } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import { Outlet, useParams } from "react-router-dom";
import type { RebalanceWithMetrics } from "../../../api/src/rebalances/rebalances.service";
import Loading from "../Loading";
import {
  type HouseholdWithAccountsAndRiskAllocation,
  useQueryHousehold,
} from "../clients/household/lib";
import FormError from "../components/FormError";
import TabContainerWithTabs from "../components/TabContainer";
import RebalanceHeader from "./RebalanceHeader";
import RebalanceNav from "./RebalanceNav";
import { useQueryRebalance } from "./lib";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";

// The check of a rebalance, as `GET /v1/rebalances/:id/check` returns it.
export type RebalanceCheckData = NonNullable<
  UnpackResponse<RebalancesController["getRebalanceCheck"]>
>["data"];

export const RebalanceContext = React.createContext<{
  // `positionSwapId`: the swap that owns a `position_swap` proposal, `null`
  // for any other rebalance (APP-1486). Absent where the provider does not
  // read it (the admin Explain viewer).
  rebalance?: RebalanceWithMetrics & { positionSwapId?: number | null };
  household?: HouseholdWithAccountsAndRiskAllocation;
  isDirty?: boolean;
  setIsDirty?: (isDirty: boolean) => void;
  // When true, every mutating control reachable in the rebalance subtree
  // (approve / reject / re-run / edit) must suppress itself. Set by the
  // read-only cross-firm admin Explain viewer
  // (s-read-only-admin-explain-019ea1c7 R6), and by `RebalanceInfo` for a
  // `position_swap` proposal, which only its swap changes (APP-1486, swap
  // spec R84). Defaults to `false` so the firm-scoped pages render unchanged
  // (AC5).
  readOnly?: boolean;
  // The check the caller gives (the admin Explain viewer, R23): `ChecksPanel`
  // shows it and sends no `/check` read. `null` is a bundle with no check.
  // Absent for every other caller, whose panel reads the check.
  givenCheck?: { check: RebalanceCheckData | null };
}>({ readOnly: false });

const RebalanceInfo = () => {
  const { rebalanceId: rebalanceIdStr } = useParams();
  const rebalanceId =
    typeof rebalanceIdStr === "undefined"
      ? undefined
      : parseInt(rebalanceIdStr);

  const [isDirty, setIsDirty] = React.useState(false);

  const {
    isPending: isPendingRebalance,
    isError: isErrorRebalance,
    data: rebalance,
  } = useQueryRebalance(rebalanceId ?? -1, {
    includeMetrics: true,
    includeTrades: true,
    enabled: typeof rebalanceId !== "undefined",
  });

  const {
    isLoading: isLoadingHousehold,
    isError: isErrorHousehold,
    data: dataHousehold,
  } = useQueryHousehold(rebalance?.householdId ?? -1, {
    includeAccounts: true,
    enabled: typeof rebalance !== "undefined" && rebalance.householdId > 0,
  });

  // Merge rebalance account risk allocation data into household accounts
  const household: HouseholdWithAccountsAndRiskAllocation | undefined = useMemo(
    () =>
      typeof dataHousehold === "undefined" || typeof rebalance === "undefined"
        ? dataHousehold
        : {
            ...dataHousehold,
            accounts: (dataHousehold.accounts ?? []).map((account) => {
              const rebalanceAccount = rebalance.accounts?.find(
                (rebalanceAcc) => rebalanceAcc.id === account.id,
              );
              return {
                ...account,
                riskAllocation: rebalanceAccount?.riskAllocation,
              };
            }),
          },
    [dataHousehold, rebalance],
  );

  return isPendingRebalance || isLoadingHousehold ? (
    <Loading />
  ) : isErrorRebalance || isErrorHousehold ? (
    <FormError message="An error occurred" />
  ) : !rebalance || !household ? (
    <Alert>Rebalance not found</Alert>
  ) : (
    <RebalanceContext.Provider
      value={{
        rebalance,
        household,
        isDirty,
        setIsDirty,
        // A `position_swap` proposal is changed only from its swap (APP-1486,
        // swap spec R84); the API refuses the rebalance routes' changes.
        readOnly: rebalance.type === "position_swap",
      }}
    >
      <RebalanceHeader />
      <Row>
        <Col>
          <TabContainerWithTabs tabs={RebalanceNav}>
            <Col>
              <Outlet />
            </Col>
          </TabContainerWithTabs>
        </Col>
      </Row>
    </RebalanceContext.Provider>
  );
};

export default RebalanceInfo;
